// client/src/App.js

import React, { useEffect } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import { io } from 'socket.io-client';
import { jwtDecode } from 'jwt-decode';
import Register from './components/Register';
import Login from './components/Login';
import SetupPage from './components/SetupPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
//import Dashboard from './components/Dashboard';
import UserProfile from './components/UserProfile';
import ProfilePage from './components/ProfilePage';
import AdminDashboard from './components/AdminDashboard';
import ModerationDashboard from './components/admin/ModerationDashboard';
import UserManagement from './components/admin/UserManagement';
import DeletedUsers from './components/DeletedUsers';
import LandingPage from './components/LandingPage';
import ConsoleCollectionPage from './components/consoles/ConsoleCollectionPage';
import GameCollectionPage from './components/games/GameCollectionPage';
import CreateAccount from './components/CreateAccount';
import LegoDashboard from './components/lego/LegoDashboard';
import GameWishlistDashboard from './components/games/GameWishlistDashboard';
import ConsoleWishlistDashboard from './components/consoles/ConsoleWishlistDashboard';
import LegoWishlistDashboard from './components/lego/LegoWishlistDashboard';
import MovieDashboard from './components/movies/MovieDashboard';
import MusicDashboard from './components/music/MusicDashboard';
import MovieWishlistDashboard from './components/movies/MovieWishlistDashboard';
import MusicWishlistDashboard from './components/music/MusicWishlistDashboard';
import BookDashboard from './components/books/BookDashboard';
import BookWishlistDashboard from './components/books/BookWishlistDashboard';
import BoardGameDashboard from './components/boardgames/BoardGameDashboard';
import BoardGameWishlistDashboard from './components/boardgames/BoardGameWishlistDashboard';
import TradingCardDashboard from './components/trading-cards/TradingCardDashboard';
import MangaDashboard from './components/manga/MangaDashboard';
import ComicsDashboard from './components/comics/ComicsDashboard';
import SchemaDesigner from './components/custom/SchemaDesigner';
import SchemaLibrary from './components/custom/SchemaLibrary';
import CustomCollectionDashboard from './components/custom/CustomCollectionDashboard';
import BacklogPage from './components/backlog/BacklogPage';
import UserList from './components/UserList';
import StatisticsPage from './components/StatisticsPage';
import DatabaseManagement from './components/DatabaseManagement';
import ImageManagement from './components/ImageManagement';
import ImageProcessingManagement from './components/admin/ImageManagement';
import EmailSettings from './components/admin/EmailSettings';
import QuotaHarvesting from './components/admin/QuotaHarvesting';
import ApiKeysSettings from './components/admin/ApiKeysSettings';
import FederationSettings from './components/admin/FederationSettings';
// [seo-strip-start]
import SeoSettings from './components/admin/SeoSettings';
import AboutPage from './components/about/AboutPage';
// [seo-strip-end]
import RemoteProfilePage from './components/federation/RemoteProfilePage';
import DownloadPage from './components/distribution/DownloadPage';
import ApiSetupPage from './components/docs/ApiSetupPage';
//import ReadOnlyUserProfile from './components/ReadOnlyUserProfile';
import { isTokenValid, checkAuthAndRedirect } from './utils/authUtils';
import UserCollections from './components/UserCollections';
import UserWishlists from './components/UserWishlists';
import ItemDetailsPage from './components/ItemDetailsPage';
import FeatureFlagsPage from './components/FeatureFlagsPage';
import ImportExport from './components/ImportExport';
import UserStatisticsPage from './components/UserStatisticsPage';
import AiChatWindow from './components/AiChatWindow';
import ThreadList from './components/messages/ThreadList';
import ThreadView from './components/messages/ThreadView';
import ChatPage from './components/chat/ChatPage';
import NotificationsPage from './components/NotificationsPage';
import WelcomePage from './components/WelcomePage';
import { FeatureFlagsProvider, useFeatureFlags } from './contexts/FeatureFlagsContext';

const App = () => {
  // Check authentication and redirect if needed
  const isAuthenticated = checkAuthAndRedirect();

  // Extract isAdmin flag from token or localStorage
  const token = localStorage.getItem('token');
  let isAdmin = false;
  let isDisabled = false;
  let isDeleted = false;
  let onboardingCompleted = false;
  
  // First try to get isAdmin from localStorage for reliability
  const storedIsAdmin = localStorage.getItem('isAdmin');
  if (storedIsAdmin === 'true') {
    isAdmin = true;
  }
  
  // If we have a token, try to decode it for additional info
  if (token) {
    try {
      // Use jwtDecode instead of manual parsing
      const decodedToken = jwtDecode(token);
      
      // If we didn't get isAdmin from localStorage, use the token value
      if (storedIsAdmin === null) {
        isAdmin = decodedToken.isAdmin || false;
      }
      
      isDisabled = decodedToken.isDisabled || false;
      isDeleted = decodedToken.isDeleted || false;
      onboardingCompleted = decodedToken.onboardingCompleted || false;
      
      // Log successful token decode
      console.log('Token decoded successfully. User is' + (isAdmin ? '' : ' not') + ' an admin.');
    } catch (error) {
      console.error('Error decoding token:', error);
      // If token is invalid, clear it
      if (!isTokenValid()) {
        localStorage.removeItem('token');
      }
    }
  }
  
  useEffect(() => {
    // Function to determine the WebSocket URL based on the environment
    const getWebSocketURL = () => {
      // Get the current hostname and port
      const hostname = window.location.hostname;
      const port = window.location.port;
      
      // Always use localhost:5000 when running locally (either in development or Docker)
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        console.log('Using local WebSocket URL: ws://localhost:5000');
        return 'ws://localhost:5000';
      }
      
      // If we're accessing via HTTPS (intercollect.app) or any HTTPS site
      if (hostname.includes('intercollect.app') || window.location.protocol === 'https:') {
        // For proxy access, use the same host with secure WebSocket protocol
        console.log('Using secure WebSocket URL for HTTPS proxy');
        return `wss://${window.location.host}`;
      }
      
      // If we're accessing via IP address directly (likely on local network)
      // Use the same hostname but with the backend port
      if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
        // When accessing via IP, use port 5000 for the WebSocket
        return `ws://${hostname}:5000`;
      }
      
      // Default fallback - use the current host with appropriate protocol
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${window.location.host}`;
    };

    // Only connect to WebSocket if specifically needed
    // Set this flag to true only when you need real-time features
    const enableWebSockets = false;
    
    if (enableWebSockets) {
      // Use the determined WebSocket URL
      const wsUrl = getWebSocketURL();
      console.log('Using WebSocket URL:', wsUrl);
      
      const socket = io(wsUrl, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        withCredentials: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });

      socket.on('connect', () => {
        console.log('Connected to SignalR Hub');
      });

      socket.on('disconnect', () => {
        console.log('Disconnected from SignalR Hub');
      });

      socket.on('error', (error) => {
        console.error('SignalR Connection Error:', error);
      });

      return () => {
        socket.disconnect();
      };
    }
    
    // Return empty cleanup function when WebSockets are disabled
    return () => {};
  }, []);

  // Wrapper component to render the AI Chat Window conditionally
  const AppWithChat = () => {
    const { featureFlags, loading: flagsLoading } = useFeatureFlags();
    const location = useLocation();

    // GA initialisation — runs once on mount
    useEffect(() => {
      fetch('/api/seo/public')
        .then(r => r.json())
        .then(data => {
          const gaId = data.googleAnalyticsId;
          if (gaId && navigator.doNotTrack !== '1') {
            ReactGA.initialize(gaId);
          }
        })
        .catch(() => {});
    }, []);

    // GA pageview on route change
    useEffect(() => {
      if (ReactGA.isInitialized) {
        ReactGA.send({ hitType: 'pageview', page: location.pathname });
      }
    }, [location]);

    return (
      <>
        <Helmet>
          <title>InterCollect</title>
        </Helmet>
        <Routes>
          {/* Public Routes */}
          <Route path="/setup" element={<SetupPage />} />
          {/* [seo-strip-start] */}
          <Route path="/about" element={<AboutPage />} />
          {/* [seo-strip-end] */}
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/docs" element={<Navigate to="/docs/api-setup" replace />} />
          <Route path="/docs/api-setup" element={<ApiSetupPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Disabled Account Route */}
          <Route 
            path="/account-disabled" 
            element={
              <div style={{ 
                textAlign: 'center', 
                padding: '50px', 
                maxWidth: '600px', 
                margin: '0 auto' 
              }}>
                <h1>Account Disabled</h1>
                <p>Your account has been disabled by an administrator. Please contact support for assistance.</p>
                <button 
                  onClick={() => {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Return to Login
                </button>
              </div>
            } 
          />

          {/* Deleted Account Route */}
          <Route 
            path="/account-deleted" 
            element={
              <div style={{ 
                textAlign: 'center', 
                padding: '50px', 
                maxWidth: '600px', 
                margin: '0 auto' 
              }}>
                <h1>Account Deleted</h1>
                <p>Your account has been deleted. Please contact an administrator if you believe this is an error.</p>
                <button 
                  onClick={() => {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Return to Login
                </button>
              </div>
            } 
          />

          {/* Conditional Root Route */}
          <Route
            path="/"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : !onboardingCompleted
                      ? <Navigate to="/welcome" />
                      : <LandingPage />
                : <Navigate to="/login" />
            }
          />

          {/* Onboarding wizard — shown once after first email verification */}
          <Route
            path="/welcome"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : <WelcomePage />
                : <Navigate to="/login" />
            }
          />

          {/* Protected Routes */}
          <Route
            path="/users/:id/collections"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <UserCollections />
                : <Navigate to="/login" />
            }
          />
          {/* User List and Collections */}
          <Route
            path="/community"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <UserList />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/users/:id"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <UserProfile mode="view" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/users/:id/edit"
            element={<Navigate to="/profile" replace />}
          />
          <Route
            path="/profile"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : <ProfilePage />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/remote-profile"
            element={
              isAuthenticated
                ? isDeleted ? <Navigate to="/account-deleted" />
                  : isDisabled ? <Navigate to="/account-disabled" />
                  : <RemoteProfilePage />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : isAdmin ? <AdminDashboard /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/moderation"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : isAdmin ? <ModerationDashboard /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/users"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : isAdmin ? <UserManagement /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/consoles"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableConsolesFeature !== false
                      ? <ConsoleCollectionPage />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/games"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableGamesFeature !== false
                      ? <GameCollectionPage />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/create-account"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <CreateAccount />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/lego"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableLegoFeature !== false
                      ? <LegoDashboard />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/movies"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableMoviesFeature !== false
                      ? <MovieDashboard />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/music"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableMusicFeature !== false
                      ? <MusicDashboard />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/wishlist/games"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableGamesFeature !== false
                      ? <GameWishlistDashboard />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/wishlist/consoles"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableConsolesFeature !== false
                      ? <ConsoleWishlistDashboard />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/wishlist/legos"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableLegoFeature !== false
                      ? <LegoWishlistDashboard />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/wishlist/movies"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableMoviesFeature !== false
                      ? <MovieWishlistDashboard />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/wishlist/music"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableMusicFeature !== false
                      ? <MusicWishlistDashboard />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/books"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableBooksFeature !== false
                      ? <BookDashboard />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/wishlist/books"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableBooksFeature !== false
                      ? <BookWishlistDashboard />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/board-games"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : flagsLoading ? null : featureFlags.enableBoardGamesFeature
                      ? <BoardGameDashboard />
                      : <Navigate to="/" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/wishlist/board-games"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : flagsLoading ? null : featureFlags.enableBoardGamesFeature
                      ? <BoardGameWishlistDashboard />
                      : <Navigate to="/" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/trading-cards"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : flagsLoading ? null : featureFlags.enableTradingCardsFeature
                      ? <TradingCardDashboard />
                      : <Navigate to="/" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/wishlist/trading-cards"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : flagsLoading ? null : featureFlags.enableTradingCardsFeature
                      ? <TradingCardDashboard wishlist={true} />
                      : <Navigate to="/" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/manga"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : flagsLoading ? null : featureFlags.enableMangaFeature
                      ? <MangaDashboard />
                      : <Navigate to="/" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/wishlist/manga"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : flagsLoading ? null : featureFlags.enableMangaFeature
                      ? <MangaDashboard wishlist={true} />
                      : <Navigate to="/" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/comics"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : flagsLoading ? null : featureFlags.enableComicsFeature
                      ? <ComicsDashboard />
                      : <Navigate to="/" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/wishlist/comics"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : flagsLoading ? null : featureFlags.enableComicsFeature
                      ? <ComicsDashboard wishlist={true} />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          {/* Custom Collections */}
          <Route
            path="/schema-library"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableCustomCollections
                      ? <SchemaLibrary />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/custom-schemas/new"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableCustomCollections
                      ? <SchemaDesigner />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/custom-schemas/:id/edit"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableCustomCollections
                      ? <SchemaDesigner />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/custom/:schemaId"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableCustomCollections
                      ? <CustomCollectionDashboard />
                      : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          {/* Backlog */}
          <Route
            path="/backlog"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : featureFlags.enableBacklog
                      ? <BacklogPage />
                      : <Navigate to="/" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/collections/:id"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : <UserCollections />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/wishlists/:id"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <UserWishlists />
                : <Navigate to="/login" />
            }
          />
          {/* Admin Edit Route */}
          <Route
            path="/admin/users/:id/edit"
            element={<Navigate to="/profile" replace />}
          />
          <Route
            path="/admin/statistics"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : isAdmin ? <StatisticsPage /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/database"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : isAdmin ? <DatabaseManagement /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/images"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : isAdmin ? <ImageProcessingManagement /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/feature-flags"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : isAdmin ? <FeatureFlagsPage /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          {/* Item Details Routes */}
          <Route
            path="/item/:itemType/:itemId"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <ItemDetailsPage />
                : <Navigate to="/login" />
            }
          />
          {/* Item Edit Routes */}
          <Route
            path="/collection/games/edit/:itemId"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <GameCollectionPage editMode={true} />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/collection/consoles/edit/:itemId"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <ConsoleCollectionPage editMode={true} />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/collection/legos/edit/:itemId"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <LegoDashboard editMode={true} />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/edit-profile/:id"
            element={<Navigate to="/profile" replace />}
          />
          <Route
            path="/import-export"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <ImportExport />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/statistics"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : <UserStatisticsPage />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/settings/customisation"
            element={<Navigate to="/profile?tab=appearance" replace />}
          />
          <Route
            path="/messages"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : <ThreadList />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/messages/thread/:threadId"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : <ThreadView />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/notifications"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : <NotificationsPage />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/feature-flags"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : isAdmin ? <FeatureFlagsPage /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/email-settings"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : isAdmin ? <EmailSettings /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/quota-harvesting"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : isAdmin ? <QuotaHarvesting /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/api-keys"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : isAdmin ? <ApiKeysSettings /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/admin/federation-settings"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : isAdmin ? <FederationSettings /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          {/* [seo-strip-start] */}
          <Route
            path="/admin/seo"
            element={
              isAuthenticated
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled
                    ? <Navigate to="/account-disabled" />
                    : isAdmin ? <SeoSettings /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
          {/* [seo-strip-end] */}
          {/* Item Details Routes */}
          <Route
            path="/item/:itemType/:itemId"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <ItemDetailsPage />
                : <Navigate to="/login" />
            }
          />
          {/* Item Edit Routes */}
          <Route
            path="/collection/games/edit/:itemId"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <GameCollectionPage editMode={true} />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/collection/consoles/edit/:itemId"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <ConsoleCollectionPage editMode={true} />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/collection/legos/edit/:itemId"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <LegoDashboard editMode={true} />
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/edit-profile/:id"
            element={<Navigate to="/profile" replace />}
          />
          <Route
            path="/import-export"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : <ImportExport />
                : <Navigate to="/login" />
            }
          />
          {/* Retro Chat Rooms */}
          {featureFlags && featureFlags.enableChatRooms && (
            <Route
              path="/chat"
              element={
                isAuthenticated
                  ? isDeleted
                    ? <Navigate to="/account-deleted" />
                    : isDisabled
                      ? <Navigate to="/account-disabled" />
                      : <ChatPage />
                  : <Navigate to="/login" />
              }
            />
          )}
          {/* Deleted Users Route */}
          <Route
            path="/admin/deleted-users"
            element={
              isAuthenticated 
                ? isDeleted
                  ? <Navigate to="/account-deleted" />
                  : isDisabled 
                    ? <Navigate to="/account-disabled" /> 
                    : isAdmin ? <DeletedUsers /> : <Navigate to="/dashboard" />
                : <Navigate to="/login" />
            }
          />
        </Routes>
        
        {/* Render AI Chat Window if feature flag is enabled and user is authenticated */}
        {featureFlags.enableAiChatFeature && isAuthenticated && !isDisabled && !isDeleted && (
          <AiChatWindow />
        )}
      </>
    );
  };

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <FeatureFlagsProvider>
          <ErrorBoundary>
            <AppWithChat />
          </ErrorBoundary>
        </FeatureFlagsProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
